/**
 * Derives the "Needs Your Attention" model for a TAS application's course-management
 * (`manage`) screen — a pure function over the application's data so it can be unit
 * tested without rendering. Mirrors the 9 distinct states in the Figma "Application
 * Review" concept (collapsed/expanded duplicates collapse to these 9).
 *
 * Task vs anchor:
 * - TASKS are actionable (Use CourseCash, Upload receipt, Submit evidence): they carry
 *   a due/use-by chip + CTA and count toward the "(N tasks pending)" header + right-rail
 *   Tasks list. A task can be `completed` (shown in the rail, NOT as an NYA card).
 * - ANCHORS are informational (submitted, evidence-submitted, approved,
 *   course-cash-used, all-caught-up): no chip/CTA, never counted.
 *
 * The panel has three zero-task shapes: the submitted anchor, the all-caught-up empty
 * state, and "hidden entirely" (`visible: false`).
 *
 * `courseCashUseByDate` comes from the active CourseCash assistance's
 * `courseCashExpiresAt` value.
 * `receiptDueDate`/`receiptUploaded`/`receiptStatus` are wired (ManagePage
 * derives them from the live CourseCash transaction — see
 * `fetchCourseCashTransactions`). `courseEndDate`/`evidenceMaxLagDays` are
 * wired the same way (ManagePage reads `COURSES_END_DATE` off the application
 * and `POST_COURSE_END_EVIDENCE_SUBMISSION_MAX_LAG_TIME` off the program
 * template) — see `deriveEvidenceDueDate` below, which mirrors tio-api's
 * `TasApplication#post_courses_end_auto_rejection_date` so the frontend chip
 * never disagrees with the deadline `TasApplicationAutoRejectionJob` actually
 * enforces.
 */

export type AttentionTaskKind =
  'use-course-cash' | 'upload-receipt' | 'submit-evidence' | 'upload-course-documents';

export type AttentionAnchorKind =
  | 'submitted'
  | 'evidence-submitted'
  | 'approved'
  | 'course-cash-used'
  | 'all-caught-up'
  | 'abandoned'
  | 'in-repayment';

export type AttentionTaskStatus = 'pending' | 'in-progress' | 'completed';

/**
 * Deadline-driven urgency for a task card (Figma: Default/Warning/Error rows
 * of the "Task: Provide Your CourseCa$h™ Receipt" spec — green/on-swipe,
 * yellow/nearing-due-date, red/blocked). Only `upload-receipt` sets this today.
 */
export type AttentionTaskUrgency = 'default' | 'warning' | 'error';

export interface AttentionTask {
  id: string;
  kind: AttentionTaskKind;
  status: AttentionTaskStatus;
  /** ISO date string for the "due mm/dd" / "use by mm/dd" chip, when known. */
  dueDate?: string | null;
  /** Icon/chip color treatment; defaults to 'default' when not set. */
  urgency?: AttentionTaskUrgency;
  /** Swipe amount in cents, when this task is tied to a specific live transaction (upload-receipt only). */
  amount?: number | null;
  /** ISO date of the swipe, when known (upload-receipt only) — distinguishes concurrent receipt tasks. */
  transactionDate?: string | null;
  /** Where the card was used (upload-receipt only) — the card-network merchant, when the
   * network named one. Null falls the detail row back to the enrolled institution. */
  merchantName?: string | null;
}

export interface AttentionAnchor {
  kind: AttentionAnchorKind;
  /** Approval total in cents, for the `approved` anchor's "Your approval total is $X". */
  approvedTotal?: number;
  /** Outstanding repayment balance in cents, for the `in-repayment` anchor. */
  outstandingRepayment?: number;
}

export interface AttentionModel {
  /** false → render no Needs Your Attention panel at all (the suppressed variant). */
  visible: boolean;
  anchors: AttentionAnchor[];
  /** All tasks incl. completed (the right-rail Tasks list); the NYA panel renders only the non-completed ones as cards. */
  tasks: AttentionTask[];
  /** Count of non-completed tasks → the "(N tasks pending)" header. */
  pendingCount: number;
}

/**
 * One CourseCash swipe still needing a receipt (Figma: "one task per swipe" —
 * each is its own card, not collapsed into a single application-wide task).
 */
export interface ReceiptTaskInput {
  /** The live transaction's id — becomes part of the task id so each swipe gets its own card. */
  id: string;
  dueDate?: string | null;
  /** The live transaction's receiptStatus, when known — drives this task's urgency. */
  status?: string | null;
  /** Swipe amount in cents — surfaced in the card title so concurrent tasks are distinguishable. */
  amount?: number | null;
  /** ISO date of the swipe — surfaced in the card body for the same reason. */
  transactionDate?: string | null;
  /** The card-network merchant this swipe was charged to, when the network named one. */
  merchantName?: string | null;
}

/**
 * The slice of `TasApplicationExtension` the derivation reads. The real model
 * satisfies this structurally; tests pass plain stubs.
 */
export interface AttentionInput {
  state: string;
  amounts?: { course_cash?: { reserved?: number; used?: number } | null } | null;
  isEvidenceRequired: boolean;
  isMissingRequiredEvidence: boolean;
  /**
   * Independent of isEvidenceRequired/isMissingRequiredEvidence (completion
   * documents) — some templates require a course grade but no documents, so
   * the submit-evidence task must key off either requirement, not just the
   * document one.
   */
  isCourseGradeRequired?: boolean;
  /**
   * The template's `attachmentsForCourseCompletionVisible`, program-type-gated by
   * ManagePage exactly as the legacy page's shouldShowAttachments gates it — the
   * completion-documents twin of `isCourseApprovalDocumentsVisible` below.
   *
   * Card-creating, never requirement-creating. `.visible` means the employee MAY
   * attach completion documents; only `.required` blocks Submit. A template
   * configured visible-but-optional therefore satisfies no requirement, so it
   * used to derive no submit-evidence task at all outside ATTEND — and with no
   * task there is no card and no upload surface, while the legacy show page
   * (which gates on visibility) offered one. Same shape as the #10292 defect this
   * input's approval-side twin exists to fix.
   */
  isCompletionDocumentsVisible?: boolean;
  isMissingCourseGrades?: boolean;
  isApproved: boolean;
  approvedTotal: number;
  /**
   * The employee still owes money back — `TasApplicationExtension#inRepayment`,
   * the same getter the legacy show page badges "In Repayment" from. Read the
   * model getter rather than re-deriving it here so the two screens cannot drift.
   */
  inRepayment?: boolean;
  /** Outstanding repayment balance in cents, shown on the in-repayment anchor. */
  outstandingRepayment?: number;
  /** Optional use-by date — the active CourseCa$h assistance's `courseCashExpiresAt`. */
  courseCashUseByDate?: string | null;
  /** The application's active/primary CourseCa$h card has been deactivated —
   * the use-course-cash task must stop inviting spend on a card that will
   * decline. */
  pcsCardDeactivated?: boolean;
  /** Raw `COURSES_END_DATE` application field (YYYY-MM-DD), when known — the
   * anchor for both the Default→Warning urgency transition and the due-date
   * math in `deriveEvidenceDueDate`. */
  courseEndDate?: string | null;
  /**
   * Raw `POST_COURSE_END_EVIDENCE_SUBMISSION_MAX_LAG_TIME` program-template
   * field (days), when known. This is per-program-template, admin-configured
   * (tio-api seed default is 45 days) — never hardcode a number here. When
   * absent, no due-date chip or urgency treatment is shown for submit-evidence
   * (mirrors "conditional, for plans with auto-reject" in the Figma spec).
   */
  evidenceMaxLagDays?: number | null;
  /**
   * Course-approval documents (classification COURSE_APPROVAL_DOCUMENTS) — the
   * legacy show page's pre-approval attachments surface. `...Visible` is the
   * template's `attachmentsForCourseApprovalVisible`, program-type-gated by
   * ManagePage exactly as the legacy page's shouldShowAttachments gates it;
   * `has...` is presence of at least one live COURSE_APPROVAL_DOCUMENTS asset
   * (course- or application-level — see courseApprovalDocumentAssets in
   * task-card/course-documents.gts). The template's REQUIRED flag is
   * deliberately not an input: required and optional surfaces derive the same
   * task (only the card's wording differs, read off the model by the card
   * itself), because a visible-but-optional surface with no upload path would
   * recreate the exact #10292 defect one flag over.
   */
  isCourseApprovalDocumentsVisible?: boolean;
  hasCourseApprovalDocuments?: boolean;
  /**
   * One upload-receipt task per live CourseCash transaction still needing a
   * receipt. When provided (even as an empty array), this is authoritative —
   * ManagePage supplies it from the real transaction list. The singular
   * receiptUploaded/receiptDueDate/receiptStatus fields below are a
   * single-task fallback for contexts with no live transaction data (dev
   * fixture gallery, unit tests).
   */
  receiptTasks?: ReceiptTaskInput[];
  receiptDueDate?: string | null;
  receiptUploaded?: boolean;
  receiptStatus?: string | null;
}

/**
 * Just-submitted, awaiting first review → the "submitted and under review" anchor.
 *
 * Exported because the rail's status line needs the same answer (ManagePage's
 * `isInReview`): the panel saying "your items have been submitted" while the rail
 * says nothing about review is the exact mismatch PM reported. One set, so the two
 * cannot drift.
 */
export const SUBMITTED_REVIEW_STATES = new Set<string>([
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
]);

/**
 * The legacy show page's `courseApprovalStates` (show.gts:147-152) — the window
 * in which that page's attachments section collects COURSE_APPROVAL_DOCUMENTS
 * (outside it the same section becomes the COMPLETION_DOCUMENTS evidence
 * surface, see its assetClassificationString). The upload-course-documents
 * task mirrors that window exactly, so a document requirement can never be
 * shown here in a state where the legacy page would already have moved on.
 */
export const COURSE_APPROVAL_STATES = new Set<string>([
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
]);

// Nothing left to do → the "you're all caught up" empty state.
const CAUGHT_UP_STATES = new Set<string>([
  'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ApplicationState.FULFILLED',
]);

/**
 * States in which submitting evidence is actually the employee's move.
 *
 * Everything earlier — CREATED/DEFINE_COURSES (still being built),
 * PENDING_COURSES_APPROVAL (sitting with an approver), COURSES_NOT_APPROVED/
 * COURSES_REJECTED/REJECTED_PER_POLICY (the courses themselves need fixing) —
 * has no approved coursework to submit evidence *for*, so a "Submit your
 * Evidence Items" card there invents work the employee cannot do and contradicts
 * the under-review anchor sitting directly above it.
 *
 * PENDING_EVIDENCE_APPROVAL is excluded for the same reason from the other side:
 * the evidence is already with a reviewer, and the evidence-submitted anchor
 * speaks for it.
 *
 * The evidence-rejected states ARE included — a returned submission is real,
 * actionable work.
 *
 * The terminal CAUGHT_UP_STATES (EVIDENCE_APPROVED, FULFILLED) are deliberately
 * absent. Nothing is submittable on a closed, paid-out request, and admitting
 * them would let a late flip of the evidence getters — an attachment deleted
 * after the fact — resurrect a pending task that then suppresses BOTH the
 * all-caught-up anchor and the fulfilled rail's completion line.
 */
export const EVIDENCE_ACTIONABLE_STATES = new Set<string>([
  'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ApplicationState.ATTEND',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
]);

/**
 * Where the evidence CARD may render, as opposed to where evidence is
 * submittable. A superset of {@link EVIDENCE_ACTIONABLE_STATES}: a program that
 * asks for documents keeps its upload surface while an approver holds the
 * request, and after the decision, so an employee can still see and add what was
 * asked for. `canSubmitEvidence` remains ATTEND-only, so the extra states are
 * read-and-upload rather than submit.
 *
 * Deliberately a separate set rather than a widened actionable one — that set is
 * exported and drives the beta home page's alert urgency split, where these
 * states must keep meaning "nothing owed".
 *
 * Drafts are absent because the manage route bounces DEFINE_COURSES into the
 * guided wizard before this renders, and abandoned/withdrawn are absent because
 * there is nothing left to evidence.
 */
const EVIDENCE_CARD_STATES = new Set<string>([
  ...EVIDENCE_ACTIONABLE_STATES,
  ...SUBMITTED_REVIEW_STATES,
  ...CAUGHT_UP_STATES,
]);

/**
 * The subset of {@link EVIDENCE_ACTIONABLE_STATES} where evidence came BACK to
 * the employee rather than not having been sent yet. Both mean "you owe an
 * action"; a return is the more urgent of the two.
 *
 * Exported alongside the set above so the beta home page's dashboard roll-up can
 * split these states by urgency without keeping its own copy of the list — a
 * second copy is exactly how the two would drift.
 */
export const EVIDENCE_RETURNED_STATES = new Set<string>([
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
]);

/*
 * PENDING_COURSES_APPROVAL is absent unconditionally, and deliberately diverges
 * from `TasApplicationExtension#canSubmitEvidence`, which admits that state when
 * the template's `courseApprovalRequired` is false.
 *
 * A carve-out mirroring that getter was added here and had to be reverted: the
 * seeded "DE" template has APPLICATION_APPROVE_COURSES_WORKFLOW.required = false
 * while still routing applications through PENDING_COURSES_APPROVAL, so the
 * carve-out fired on an ordinary request and put "Submit your Evidence Items"
 * directly beneath the "submitted and under review" anchor — the exact defect
 * the gate exists to prevent.
 *
 * The two surfaces are answering different questions. `canSubmitEvidence` gates
 * whether a submission would be *accepted*; this set decides what to *ask the
 * employee to do now*. While a request is with a reviewer the answer is nothing,
 * whatever the workflow config says. Nobody is stranded: the task appears as
 * soon as the state advances.
 */

// A rejected/changes-needed receipt blocks progress the same way "user on hold"
// does in the Figma spec — treat both as the Error/red urgency.
const BLOCKED_RECEIPT_STATUSES = new Set(['rejected', 'changes_needed']);

// A due date this close (or passed) reads as Warning/Error even before the
// receipt is rejected outright.
const RECEIPT_WARNING_WINDOW_DAYS = 3;

/**
 * Default/Warning/Error urgency for the upload-receipt task (Figma: Green on
 * swipe, Yellow nearing due date, Red once blocked/overdue).
 */
export function deriveReceiptUrgency(
  receiptStatus: string | null | undefined,
  receiptDueDate: string | null | undefined
): AttentionTaskUrgency {
  if (receiptStatus && BLOCKED_RECEIPT_STATUSES.has(receiptStatus)) {
    return 'error';
  }
  if (receiptDueDate) {
    // Parse the calendar date directly (see chipFor's note in
    // needs-your-attention.gts) to avoid a UTC-parsed off-by-one day.
    const datePart = receiptDueDate.split('T')[0] ?? '';
    const due = new Date(`${datePart}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return 'error';
    if (diffDays <= RECEIPT_WARNING_WINDOW_DAYS) return 'warning';
  }
  return 'default';
}

// Once the due date is this close (or passed), submit-evidence reads as
// Error — matches the 7-day mark tio-api's deadline-reminder emails already
// use (tas_application_submit_evidence_deadline_reminder_action.rb) before
// TasApplicationAutoRejectionJob auto-rejects the request.
const EVIDENCE_ERROR_WINDOW_DAYS = 7;

/** YYYY-MM-DD from a Date's LOCAL calendar fields — not toISOString(), which
 * converts to UTC and can shift the calendar day depending on the runner's
 * timezone offset (same concern as chipFor's note in needs-your-attention.gts). */
function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Whole calendar days from `from` to `to`, via UTC-normalized calendar
 * fields rather than dividing the millisecond gap between two local-time
 * Date objects by 24h — a DST fall-back day is 25 local hours, which can
 * round a 7-calendar-day gap up to 8 and delay the Error tier by a day. */
function calendarDaysBetween(from: Date, to: Date): number {
  const utcFrom = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const utcTo = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((utcTo - utcFrom) / (1000 * 60 * 60 * 24));
}

/**
 * Mirrors tio-api's `TasApplication#post_courses_end_auto_rejection_date`
 * (`courses_end_date + POST_COURSE_END_EVIDENCE_SUBMISSION_MAX_LAG_TIME days`)
 * so the "due mm/dd" chip never disagrees with the deadline the backend will
 * actually enforce. Returns null when either input is missing — some program
 * templates may not have the lag-time field configured at all ("conditional,
 * for plans with auto-reject" in the Figma spec), in which case no due date
 * is shown.
 */
export function deriveEvidenceDueDate(
  courseEndDate: string | null | undefined,
  maxLagDays: number | null | undefined
): string | null {
  if (!courseEndDate || maxLagDays == null) {
    return null;
  }
  const datePart = courseEndDate.split('T')[0] ?? '';
  const endDate = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(endDate.getTime())) {
    return null;
  }
  endDate.setDate(endDate.getDate() + maxLagDays);
  return formatLocalDate(endDate);
}

/**
 * Default/Warning/Error urgency for the submit-evidence task (Figma "Task
 * Group: Evidence Submission" spec): Default before the course end date,
 * Warning once it's passed, Error inside the EVIDENCE_ERROR_WINDOW_DAYS
 * window before (or past) the computed due date. Returns 'default' whenever
 * `courseEndDate` is unknown — deriveAttention treats a 'default' result as
 * "no urgency override" so the task keeps its plain teal treatment (see the
 * call site below).
 */
export function deriveEvidenceUrgency(
  courseEndDate: string | null | undefined,
  dueDate: string | null | undefined
): AttentionTaskUrgency {
  if (!courseEndDate) {
    return 'default';
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDatePart = courseEndDate.split('T')[0] ?? '';
  const endDate = new Date(`${endDatePart}T00:00:00`);
  if (today < endDate) {
    return 'default';
  }
  if (!dueDate) {
    return 'warning';
  }
  const duePart = dueDate.split('T')[0] ?? '';
  const due = new Date(`${duePart}T00:00:00`);
  const diffDays = calendarDaysBetween(today, due);
  return diffDays <= EVIDENCE_ERROR_WINDOW_DAYS ? 'error' : 'warning';
}

export function deriveAttention(app: AttentionInput): AttentionModel {
  // Money owed back outlives every other state. A repayment is not a task —
  // there is no employee-facing repay action anywhere in this app — so it is an
  // anchor, and it must be built before the short-circuits below so a closed
  // request cannot quietly drop it.
  const repaymentAnchors: AttentionAnchor[] = app.inRepayment
    ? [{ kind: 'in-repayment', outstandingRepayment: app.outstandingRepayment }]
    : [];

  // Terminal, system-closed state — none of the actionable tasks below are
  // still relevant (e.g. an unspent CourseCash card or a pending evidence
  // upload from before the request was abandoned), so short-circuit before
  // any of them get built rather than merely hiding the now-stale anchors.
  // Repayment is the exception: withdrawing a request does not clear a debt.
  if (app.state === 'TAS.ApplicationState.ABANDONED') {
    return {
      visible: true,
      anchors: [...repaymentAnchors, { kind: 'abandoned' }],
      tasks: [],
      pendingCount: 0,
    };
  }

  const tasks: AttentionTask[] = [];
  // Repayment leads: an outstanding balance is the most consequential thing on
  // the panel, and being non-empty it also keeps the zero-state branch below
  // from replacing everything with the all-caught-up anchor.
  const anchors: AttentionAnchor[] = [...repaymentAnchors];

  const reserved = app.amounts?.course_cash?.reserved ?? 0;
  const used = app.amounts?.course_cash?.used ?? 0;
  // `reserved` is the CURRENT remaining balance, not the original allocation, so
  // it drops to 0 on a fully spent card. Gating on `reserved > 0` alone therefore
  // meant "has UNSPENT funds", which silently dropped the use-course-cash task,
  // the used anchor, and the receipt tasks off exactly the cards that had been
  // spent down — the state those cards exist to report. Same reasoning as the
  // pcsCards gate in manage-fixtures.ts.
  const hasCourseCash = reserved > 0 || used > 0;
  const courseCashUsed = used > 0;

  // Use CourseCash — present whenever a card is issued. Completes once spent; while
  // completed it stays in the rail list but is filtered out of the NYA cards.
  // A deactivated card also reads completed — never pending — so the panel stops
  // inviting a swipe that would decline, while the completed drawer (spent/
  // remaining/activity) stays reachable; the page-level banner explains why
  // Its use-by chip is meaningless on a dead card, so it drops too.
  if (hasCourseCash) {
    const cardDeactivated = app.pcsCardDeactivated ?? false;
    tasks.push({
      id: 'use-course-cash',
      kind: 'use-course-cash',
      status: courseCashUsed || cardDeactivated ? 'completed' : 'pending',
      dueDate: cardDeactivated ? null : (app.courseCashUseByDate ?? null),
    });
  }

  // Upload CourseCash receipt — one task per swipe still needing a receipt.
  if (courseCashUsed) {
    if (app.receiptTasks) {
      for (const receiptTask of app.receiptTasks) {
        tasks.push({
          id: `upload-receipt-${receiptTask.id}`,
          kind: 'upload-receipt',
          status: 'pending',
          dueDate: receiptTask.dueDate ?? null,
          urgency: deriveReceiptUrgency(receiptTask.status, receiptTask.dueDate),
          amount: receiptTask.amount ?? null,
          transactionDate: receiptTask.transactionDate ?? null,
          merchantName: receiptTask.merchantName ?? null,
        });
      }
    } else if (!app.receiptUploaded) {
      tasks.push({
        id: 'upload-receipt',
        kind: 'upload-receipt',
        status: 'pending',
        dueDate: app.receiptDueDate ?? null,
        urgency: deriveReceiptUrgency(app.receiptStatus, app.receiptDueDate),
      });
    }
  }

  // Course-approval documents — the pre-approval window's attachment surface
  // (#10292; mirrors the legacy show page's courseApprovalStates ×
  // shouldShowAttachments × isAttachmentRequired). Visibility is the
  // template's call alone; status is document PRESENCE alone, and the
  // template's required flag drives only the card's wording (read off the
  // model by the card itself — see AttentionInput's doc for why it is not an
  // input here). Present even during PENDING_COURSES_APPROVAL: these documents
  // are what the approver is reviewing, so uploading them while the request
  // sits with a reviewer is real, useful work, exactly as the legacy page
  // allows — submit-evidence below now spans review states for the same reason.
  // A visible-but-optional surface with no documents is still 'pending' rather
  // than hidden: this card is the only upload path on the screen, so hiding it
  // would leave optional uploads unsatisfiable — the very defect this task
  // exists to fix. Never carries an urgency or due date: course-approval
  // documents have no deadline machinery (nothing like evidence's
  // auto-rejection date).
  if (COURSE_APPROVAL_STATES.has(app.state) && app.isCourseApprovalDocumentsVisible) {
    tasks.push({
      id: 'upload-course-documents',
      kind: 'upload-course-documents',
      status: app.hasCourseApprovalDocuments ? 'completed' : 'pending',
    });
  }

  // Submit evidence — the checklist card whenever the template asks for
  // completion documents AND/OR a course grade (independent requirements — a
  // template can require a grade with no documents, or vice versa), and
  // unconditionally at ATTEND. The requirements decide what the card asks
  // for, but never whether submission itself is offered: firing the
  // request-evidence-approval transition is a required step for every
  // application, and this card's footer is the only place the Submit button
  // lives. Gating the card on the requirements therefore dead-ended
  // templates that require neither (or applications whose evidence
  // requirement was waived) — no card, no Submit, while the approved anchor
  // above made the page read "already approved" (#10395; QA 8/7). The legacy
  // page's showSubmitForEvidenceReview is exactly `state === ATTEND`, with
  // no requirements gate. In earlier/returned states the requirements gate
  // stands: nothing to upload and no legal submit transition means no card.
  // The ATTEND arm also keeps the card once everything required is in —
  // the card must not disappear the instant the missing-ness flips false,
  // or the Submit button vanishes with it.
  const evidenceOrGradesRequired = app.isEvidenceRequired || Boolean(app.isCourseGradeRequired);
  const evidenceOrGradesMissing =
    app.isMissingRequiredEvidence ||
    Boolean(app.isCourseGradeRequired && app.isMissingCourseGrades);
  // Any of the three signals counts as "this program asks for evidence": a hard
  // requirement, a required grade, or categories the template merely SHOWS
  // (`.visible` on, `.required` off). Presence of a requirement is now enough on
  // its own — the card no longer waits for something to be missing, so it does
  // not vanish the moment the last document lands, and it survives into the
  // review and settled states below.
  // Widening visibility cannot widen what the employee can DO. `canSubmitEvidence`
  // requires ATTEND ahead of its `ignorePolicies` bypass, and the card withholds
  // Submit rather than disabling it outside ATTEND, so the extra states are
  // read-only. Where the course-approval card owns the document surface the
  // evidence uploader stands down with it, leaving one dropzone on the screen;
  // both return at ATTEND, which no course-approval state is.
  //
  // Review states are here because the approval-documents task above already
  // renders while an approver holds the request: uploading what the reviewer is
  // waiting for is real work, and product asked for the same of evidence.
  const hasEvidenceRequirements =
    evidenceOrGradesRequired || Boolean(app.isCompletionDocumentsVisible);
  if (
    EVIDENCE_CARD_STATES.has(app.state) &&
    (hasEvidenceRequirements || app.state === 'TAS.ApplicationState.ATTEND')
  ) {
    const evidenceDueDate = deriveEvidenceDueDate(app.courseEndDate, app.evidenceMaxLagDays);
    const evidenceUrgency = deriveEvidenceUrgency(app.courseEndDate, evidenceDueDate);
    tasks.push({
      id: 'submit-evidence',
      kind: 'submit-evidence',
      // Never 'completed': the panel filters that out of taskRows and there is no
      // completed-drawer site for this kind, so marking it done would hide the
      // card in exactly the settled states it is meant to stay visible for.
      // pendingCount excludes it there instead.
      status: evidenceOrGradesMissing ? 'pending' : 'in-progress',
      // A settled or under-review request has no deadline left to advertise.
      dueDate: EVIDENCE_ACTIONABLE_STATES.has(app.state) ? evidenceDueDate : null,
      // 'default' means "no urgency override" here — leaving it unset keeps
      // the task's plain teal treatment (TASK_COLORS/'info' chip in
      // needs-your-attention.gts), matching the Figma "Default" variant,
      // which is teal, not the green URGENCY_ICON_COLORS.default is tuned
      // for upload-receipt's "on swipe, all good" meaning.
      urgency:
        EVIDENCE_ACTIONABLE_STATES.has(app.state) && evidenceUrgency !== 'default'
          ? evidenceUrgency
          : undefined,
    });
  }

  // Informational anchors render above the action cards. `isApproved` stays true from
  // COURSES_APPROVED all the way through FULFILLED, so once the application has moved
  // into its own review state (SUBMITTED_REVIEW_STATES), a terminal state
  // (CAUGHT_UP_STATES), or CourseCash has already been used, that anchor takes
  // priority over re-showing the courses-approved congratulations (Figma: the
  // "CourseCash used" NYA state shows only the used anchor, not both).
  if (
    app.isApproved &&
    !SUBMITTED_REVIEW_STATES.has(app.state) &&
    !CAUGHT_UP_STATES.has(app.state) &&
    !courseCashUsed
  ) {
    anchors.push({ kind: 'approved', approvedTotal: app.approvedTotal });
  }
  // Same review/terminal-state exclusion as the approved anchor above: `used`
  // stays true forever once spent, so without this a PENDING_EVIDENCE_APPROVAL/
  // EVIDENCE_APPROVED/FULFILLED application would keep this anchor forever and
  // never reach the submitted/all-caught-up zero-state branches below.
  if (
    courseCashUsed &&
    !SUBMITTED_REVIEW_STATES.has(app.state) &&
    !CAUGHT_UP_STATES.has(app.state)
  ) {
    anchors.push({ kind: 'course-cash-used' });
  }
  // The application/evidence was just submitted and is awaiting the approver's
  // decision — this must surface regardless of any other task still pending
  // (e.g. an unspent CourseCash card), or a request can sit in review with
  // nothing on the panel acknowledging it. The submit-evidence task does still
  // render here, but read-only and outside pendingCount, so it never announces
  // itself as work the employee owes.
  if (SUBMITTED_REVIEW_STATES.has(app.state)) {
    const isEvidenceReview = app.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL';
    anchors.push({ kind: isEvidenceReview ? 'evidence-submitted' : 'submitted' });
  }

  // The evidence card now renders outside the actionable states as a record and
  // an upload surface, but the employee owes nothing there — an approver holds
  // the request under review, and a settled one is closed. Counting it would
  // also suppress the all-caught-up anchor and the fulfilled rail's completion
  // line, both of which key on this reaching zero.
  const pendingCount = tasks.filter(
    (task) =>
      task.status !== 'completed' &&
      !(task.kind === 'submit-evidence' && !EVIDENCE_ACTIONABLE_STATES.has(app.state))
  ).length;

  // Zero actionable + zero anchor → the one remaining quiet variant.
  if (pendingCount === 0 && anchors.length === 0) {
    if (CAUGHT_UP_STATES.has(app.state)) {
      return { visible: true, anchors: [{ kind: 'all-caught-up' }], tasks, pendingCount: 0 };
    }
    // Under review with nothing to surface → suppress the panel entirely.
    return { visible: false, anchors: [], tasks, pendingCount: 0 };
  }

  return { visible: true, anchors, tasks, pendingCount };
}
